/* eslint-disable max-lines */
import {
  Button,
  FormController,
  FormLabel,
  Input,
  Link,
  LoadingButton,
  Modal,
  ModalBody,
  ModalHeader,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { applyDeliveryTrackerReturnWindows } from '../../utils/returns/returns-fg-helper';
import { getDeliveryTrackerReturnTypes } from '../../utils/returns/returns-helper';
import { FeatureSwitches } from '../../constants/feature-switches';
import { getGuestOrderTracking } from '../../api/oms/guest-order-tracking';
import { guestOrderTrackingErrors } from '../../constants/guest-order-tracking-error';
import { GetDeliveryTrackerInfo } from '../../hooks/delivery-tracker-hooks';
import { IFormData } from '../../models/guest-order-tracking';
import { IOrderDetailsResponse } from '../../models/order-details';
import {
  handleDisableBtn,
  makeGuestRequestBody,
} from '../../utils/guest-order-tracking-utils';
import { addPhoneHyphens } from '../../utils/order-history/form-utils';
import '../shared/ButtonFix.scss';
import { cancelEligibilityInitialCall } from '../../hooks/cancel-items-hooks';
import { useOrderContext, useCommonContext } from '../../hooks/useContexts';

export const TrackOrderForm = ({
  setFormData,
  setFormSubmitted,
  orderId,
  customerType,
}: {
  setFormData: (value: IOrderDetailsResponse | undefined | null) => void;
  setFormSubmitted: (value: boolean) => void;
  orderId: string;
  customerType: string;
}) => {
  const { userID } = useThdCustomer();
  const [isLoading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [inputError, setInputError] = useState({
    orderNumber: '',
    phoneNumber: '',
    email: '',
  });
  const { setCancellationReasonCodes, setReasonCodesApiError } =
    useOrderContext();
  const { isLoggedIn, isExchangeCustomer } = useThdCustomer();
  const isAuthTHDUser = isLoggedIn && !isExchangeCustomer;
  const trackerGatedReturnTypes = getDeliveryTrackerReturnTypes();
  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      orderNumber: '',
      phoneNumber: '',
      email: '',
      tryAgain404: false,
    },
  });
  const watchOrderNumber = watch('orderNumber');
  const watchPhoneNumber = watch('phoneNumber');
  const watchEmail = watch('email');
  const theme = useTheme(TrackOrderForm);
  const { hideOrderSignIn } = theme.props;

  const { timezone } = useCommonContext();

  useEffect(() => {
    if (orderId !== 'null' && orderId !== '') {
      setValue('orderNumber', orderId);
    }
    if (
      watchOrderNumber.length === 10 ||
      watchOrderNumber.length === 11 ||
      watchOrderNumber.length === 12 ||
      watchOrderNumber.length === 13
    ) {
      setInputError((prevState) => ({
        ...prevState,
        orderNumber: 'success',
      }));
      if (watchEmail.length > 0)
        setInputError((prevState) => ({
          ...prevState,
          phoneNumber: 'success',
          email: 'success',
        }));
      clearErrors();
    }
    if (
      inputError.orderNumber === 'success' &&
      (watchOrderNumber.length < 10 || watchOrderNumber.length > 12)
    ) {
      setInputError((prevState) => ({
        ...prevState,
        orderNumber: 'error',
      }));
      setError('orderNumber', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchOrderNumber]);

  useEffect(() => {
    if (watchPhoneNumber.length === 12) {
      setInputError((prevState) => ({
        ...prevState,
        phoneNumber: 'success',
        email: 'success',
      }));
      clearErrors();
    }
    if (
      inputError.phoneNumber === 'success' &&
      watchPhoneNumber.length !== 12
    ) {
      setInputError((prevState) => ({
        ...prevState,
        phoneNumber: 'error',
      }));
      setError('phoneNumber', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchPhoneNumber]);

  useEffect(() => {
    if (watchEmail.length > 6 && watchEmail.includes('.')) {
      if (watchEmail.includes('@')) {
        setInputError((prevState) => ({
          ...prevState,
          phoneNumber: 'success',
          email: 'success',
        }));
        if (watchOrderNumber.length > 0)
          setInputError((prevState) => ({
            ...prevState,
            orderNumber: 'success',
          }));
        clearErrors();
      } else {
        setInputError((prevState) => ({
          ...prevState,
          email: 'error',
        }));
        setError('email', { type: 'invalid', message: 'invalid entry' });
      }
    }
    if (inputError.email === 'success' && watchEmail.length < 7) {
      setInputError((prevState) => ({
        ...prevState,
        email: 'error',
      }));
      setError('email', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchEmail]);

  const onFormSubmit: SubmitHandler<IFormData> = async ({
    orderNumber,
    phoneNumber,
    email,
  }) => {
    clearErrors();
    try {
      setLoading(true);
      let request;
      if (orderNumber && (phoneNumber || email)) {
        request = makeGuestRequestBody(
          orderNumber,
          email,
          phoneNumber,
          timezone,
        );

        const response = await getGuestOrderTracking(request);
        const status = response ? response.status : null;
        if (status === null) {
          setError('tryAgain404', {
            type: 'failed',
            message: 'failed request',
          });
          return;
        }
        response.data.traceId = response.headers?.traceid;
        // Temporary variable to manage intermediate form data before updating the state
        let tempFormData: IOrderDetailsResponse | undefined;

        try {
          // Attempt to get delivery tracking data and deep copy into order details
          const deliveryTrackingData = await GetDeliveryTrackerInfo(
            response.data,
            userID,
          );
          // Update OMS's lineItem returnWindowEndDate with delivery tracker's
          // returnWindowEndDate for any eligible fulfillment group
          tempFormData = applyDeliveryTrackerReturnWindows(
            deliveryTrackingData,
            trackerGatedReturnTypes,
          );
        } catch (_error) {
          // If delivery tracking fails, just set the order details data
          tempFormData = response.data;
        }

        // Eligibility call
        try {
          if (
            FeatureSwitches.checkCancelEligibilityOnFetchOrder &&
            tempFormData
          ) {
            const updatedFulfillmentGroups = await cancelEligibilityInitialCall(
              tempFormData as IOrderDetailsResponse,
              customerType,
              isAuthTHDUser,
              userID,
              setCancellationReasonCodes,
              setReasonCodesApiError,
            );

            setFormData({
              ...(tempFormData as IOrderDetailsResponse),
              fulfillmentGroups: updatedFulfillmentGroups,
            });
          } else {
            setFormData(tempFormData as IOrderDetailsResponse | undefined);
          }
        } catch (_error) {
          // If eligibility call fails, preserve the existing tempFormData (including any delivery tracker updates)
          // No changes to tempFormData here to avoid discarding prior enrichments.
          setFormData(tempFormData);
        } finally {
          setFormSubmitted(true);
        }
        return;
      }
      setError('tryAgain404', { type: 'failed', message: 'failed request' });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setLoading(false);
      if (!('tryAgain404' in errors)) {
        if (watchEmail.length > 0) {
          setInputError({
            email: 'error',
            orderNumber: '',
            phoneNumber: '',
          });
          setError('orderNumber', { type: '404', message: 'invalid entry' });
        } else if (watchPhoneNumber.length > 0) {
          setInputError({
            email: '',
            orderNumber: '',
            phoneNumber: 'error',
          });
          setError('orderNumber', { type: '404', message: 'invalid entry' });
        }
      } else {
        reset();
      }
    }
  };

  return (
    <div className="sui-flex sui-justify-center sui-mt-8">
      <div
        style={{
          width: '450px',
          height: '336px',
        }}
        className="sui-flex sui-flex-col"
      >
        <Typography variant="h2">Look up by Order Number</Typography>
        <div className="sui-flex sui-flex-col sui-gap-8">
          <FormController
            className="sui-mt-6"
            fullWidth
            status={inputError.orderNumber || (errors?.orderNumber && 'error')}
          >
            <FormLabel htmlFor="orderNumber">Order Number</FormLabel>
            <Input
              aria-describedby="orderNumber-input"
              fullWidth
              id="orderNumber"
              inputProps={{
                maxLength: 13,
              }}
              {...register('orderNumber', {
                required: true,
                minLength: 9,
                onChange: (event: { target: { value: string } }) =>
                  setValue('orderNumber', event.target.value.toUpperCase()),
                onBlur: (event: { target: { value: string } }) =>
                  setValue('orderNumber', event.target.value.toUpperCase()),
              })}
              placeholder="e.g. W123456789 or H0121-53379"
            />
            {inputError.orderNumber === 'error' && (
              <div className="sui-flex sui-flex-col sui-gap-8 sui-mt-2">
                <Typography variant="body-xs" color="danger">
                  {(['required', 'minLength', 'invalid', 'pattern'].includes(
                    errors?.orderNumber?.type ?? 'invalid',
                  ) ||
                    errors?.orderNumber?.type === 'tryAgain') &&
                    guestOrderTrackingErrors.orderNumberNotFound}
                  <br />
                  {(['required', 'minLength', 'invalid', 'pattern'].includes(
                    errors?.orderNumber?.type ?? 'invalid',
                  ) ||
                    inputError.orderNumber === 'error') &&
                    guestOrderTrackingErrors.onlineOrdersInvalidFormat}
                  <br />
                  {(['required', 'minLength', 'invalid', 'pattern'].includes(
                    errors?.orderNumber?.type ?? 'invalid',
                  ) ||
                    inputError.orderNumber === 'error') &&
                    guestOrderTrackingErrors.instoreOrdersInvalidFormat}
                </Typography>
                <Typography variant="body-xs">
                  You can find your order number in your confirmation email or
                  your{' '}
                  <Link onClick={() => setOpenModal(true)} variant="body-xs">
                    in-store receipt
                  </Link>
                </Typography>
              </div>
            )}
          </FormController>
          {watchOrderNumber[0] === 'H' && (
            <FormController
              fullWidth
              status={
                inputError.phoneNumber || (errors?.phoneNumber && 'error')
              }
            >
              <FormLabel htmlFor="phoneNumber">Phone</FormLabel>
              <Input
                aria-describedby="phoneNumber-input"
                fullWidth
                id="phoneNumber"
                inputProps={{
                  maxLength: 12,
                }}
                {...register('phoneNumber', {
                  required: true,
                  minLength: 12,
                  pattern:
                    /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                  onChange: (event: { target: { value: string } }) =>
                    setValue(
                      'phoneNumber',
                      addPhoneHyphens(event.target.value),
                    ),
                  onBlur: (event: { target: { value: string } }) =>
                    setValue(
                      'phoneNumber',
                      addPhoneHyphens(event.target.value),
                    ),
                })}
                placeholder="(        )         -"
              />
              {(inputError.phoneNumber === 'error' || errors.phoneNumber) && (
                <div className="sui-mt-2">
                  <Typography variant="body-xs" color="danger">
                    {guestOrderTrackingErrors.tryAgainPhone}
                  </Typography>
                </div>
              )}
            </FormController>
          )}
          {(watchOrderNumber[0] === 'W' || watchOrderNumber[0] === 'C') && (
            <FormController
              fullWidth
              status={inputError.email || (errors?.email && 'error')}
            >
              <FormLabel htmlFor="email">Email</FormLabel>
              <Input
                aria-describedby="email-input"
                fullWidth
                id="email"
                {...register('email', {
                  required: true,
                  minLength: 7,
                  pattern: /^.+@[^.].*\.[a-zA-Z]{2,}$/,
                  onChange: (event: { target: { value: string } }) =>
                    setValue('email', event.target.value),
                  onBlur: (event: { target: { value: string } }) =>
                    setValue('email', event.target.value),
                })}
                placeholder="you@domain.com"
              />
              {(inputError.email === 'error' || errors.email) && (
                <div className="sui-mt-2">
                  <Typography variant="body-xs" color="danger">
                    {guestOrderTrackingErrors.tryAgainEmail}
                  </Typography>
                </div>
              )}
            </FormController>
          )}
          <div className="ButtonGroup sui-min-w-full sui-flex sui-gap-3 sui-flex-col md:sui-flex-row lg:sui-flex-row">
            <LoadingButton
              disabled={handleDisableBtn(errors, inputError)}
              onClick={handleSubmit(onFormSubmit)}
              variant="primary"
              loading={isLoading}
            >
              Track Order
            </LoadingButton>
          </div>
          {!hideOrderSignIn && (
            <>
              <div className="sui-flex sui-justify-between sui-items-center">
                <div
                  style={{
                    height: '1px',
                    width: '12rem',
                    backgroundColor: '#999',
                  }}
                />
                <Typography variant="body-base" align="center">
                  Have an account with us?
                </Typography>
                <div
                  style={{
                    height: '1px',
                    width: '12rem',
                    backgroundColor: '#999',
                  }}
                />
              </div>
              <div className="ButtonGroup sui-min-w-full sui-flex sui-gap-3 sui-flex-col md:sui-flex-row lg:sui-flex-row">
                <Button
                  href="https://www.homedepot.com/auth/view/signin?redirect=/order/view/summary"
                  variant="secondary"
                >
                  Sign in to Your Account
                </Button>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-evenly',
                  alignItems: 'center',
                }}
              >
                <Link
                  href="https://www.homedepot.com/c/PH_MyAccount"
                  target="_blank"
                  variant="body-xs"
                  align="center"
                >
                  My Account Terms & Conditions
                </Link>
                <Typography variant="body-base"> | </Typography>
                <Link
                  href="https://www.homedepot.com/c/Privacy_Security"
                  target="_blank"
                  variant="body-xs"
                  align="center"
                >
                  Privacy & Security Statement
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      <Modal open={openModal} onClose={() => setOpenModal(false)} size="sm">
        <ModalHeader onClose={() => setOpenModal(false)}>
          <Typography variant="h4">
            Finding Your Order & Phone Numbers
          </Typography>
        </ModalHeader>
        <ModalBody>
          <img
            src="https://assets.thdstatic.com/images/v1/instore-help.png"
            alt="Receipt Example"
            width="592px"
            height="312px"
            loading="lazy"
            decoding="async"
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

TrackOrderForm.displayName = 'TrackOrderForm';
